import { useState, useRef, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { authApi } from './api';

const ALLOWED_ROLES = ['superadmin', 'admin'];

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [userId, setUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (requires2FA && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [requires2FA]);

  const completeLogin = async (token) => {
    localStorage.setItem('stats_token', token);

    try {
      const userRes = await authApi.getUserInfo();
      const user = userRes.data;
      const role = user.role?.toLowerCase();

      if (!ALLOWED_ROLES.includes(role)) {
        localStorage.removeItem('stats_token');
        setError(`Access denied. Only administrators can access statistics. Your role: ${user.role}`);
        setRequires2FA(false);
        setTwoFactorCode('');
        return;
      }

      localStorage.setItem('stats_user', JSON.stringify({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
      }));
      onLogin();
    } catch (err) {
      localStorage.removeItem('stats_token');
      setError('Failed to verify user permissions');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.login(email, password);

      if (res.data?.requiresTwoFactor) {
        setTempToken(res.data.tempToken);
        setUserId(res.data.userId);
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (res.data?.token) {
        await completeLogin(res.data.token);
      } else {
        setError('Unexpected response from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.verify2fa(tempToken, userId, twoFactorCode);

      if (res.data?.token) {
        await completeLogin(res.data.token);
      } else {
        setError('Unexpected response from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid authentication code');
      setTwoFactorCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTempToken('');
    setUserId(null);
    setTwoFactorCode('');
    setError('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f2f5' }}>
      <Card sx={{ width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 56, height: 56,
              bgcolor: requires2FA ? '#2e7d32' : '#0d3c61',
              borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              transition: 'background-color 0.3s',
            }}>
              {requires2FA
                ? <SecurityIcon sx={{ color: '#fff', fontSize: 28 }} />
                : <LockOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
              }
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0d3c61' }}>AirMaxx Stats</Typography>
            <Typography variant="body2" color="text.secondary">
              {requires2FA ? 'Enter your authentication code' : 'Sign in with your CRM account'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Administrators only
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!requires2FA ? (
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} required autoFocus
              />
              <TextField
                fullWidth label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} required
              />
              <Button
                fullWidth type="submit" variant="contained" size="large" disabled={loading}
                sx={{ bgcolor: '#0d3c61', '&:hover': { bgcolor: '#0a2d4a' }, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Open your authenticator app and enter the 6-digit code
              </Typography>
              <TextField
                fullWidth
                label="Authentication Code"
                value={twoFactorCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(val);
                }}
                inputRef={codeInputRef}
                sx={{ mb: 3 }}
                required
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 },
                }}
                placeholder="000000"
              />
              <Button
                fullWidth type="submit" variant="contained" size="large"
                disabled={loading || twoFactorCode.length < 6}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, py: 1.5, mb: 1.5 }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Verify'}
              </Button>
              <Button
                fullWidth variant="text" size="small" onClick={handleBack}
                sx={{ color: '#666' }}
              >
                Back to login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
